import { css, cx } from "@linaria/core"
import {
  defaultMarkdownParser,
  defaultMarkdownSerializer,
  MarkdownParser,
  MarkdownSerializer,
  schema as markdownSchema
} from "prosemirror-markdown"
import { Schema } from "prosemirror-model"
import { addListNodes } from "prosemirror-schema-list"


import { Blockquote, Code, HeadingStyles, Li, Link, Ol, Pre, Strong, Ul } from "@/styles/Markdown.ts"

import type { Mark, MarkSpec, Node } from "prosemirror-model"

const baseNodes = markdownSchema.spec.nodes
const customNodes = addListNodes(baseNodes, "paragraph block*", "block")
  .update("heading", {
    ...baseNodes.get("heading"),
    toDOM(node: Node) {
      const level = node.attrs.level
      let className = ""
      switch (level) {
        case 1:
          className = cx(HeadingStyles.Heading1, ProseMirrorHeading)
          break
        case 2:
          className = cx(HeadingStyles.Heading2, ProseMirrorHeading)
          break
        case 3:
          className = cx(HeadingStyles.Heading3, ProseMirrorHeading)
          break
        case 4:
          className = cx(HeadingStyles.Heading4, ProseMirrorHeading)
          break
        case 5:
          className = cx(HeadingStyles.Heading5, ProseMirrorHeading)
          break
        case 6:
          className = cx(HeadingStyles.Heading6, ProseMirrorHeading)
          break
        default:
          className = cx(HeadingStyles.Heading1, ProseMirrorHeading)
          break
      }
      return [`h${level}`, { class: className }, 0]
    }
  })
  .update("blockquote", {
    ...baseNodes.get("blockquote"),
    toDOM() {
      return ["blockquote", { class: Blockquote }, 0]
    }
  })
  .update("code_block", {
    ...baseNodes.get("code_block"),
    toDOM() {
      return ["pre", { class: cx(Pre, ProseMirrorPre) }, ["code", { class: Code }, 0]]
    }
  })
  .update("ordered_list", {
    ...baseNodes.get("ordered_list"),
    toDOM() {
      return ["ol", { class: cx(Ol, ProseMirrorOl) }, 0]
    }
  })
  .update("bullet_list", {
    ...baseNodes.get("bullet_list"),
    toDOM() {
      return ["ul", { class: cx(Ul, ProseMirrorUl) }, 0]
    }
  })
  .update("list_item", {
    ...baseNodes.get("list_item"),
    toDOM() {
      return ["li", { class: Li }, 0]
    }
  })
  .update("image", {
    ...baseNodes.get("image"),
    toDOM(node: Node) {
      return ["img", { class: Image, src: node.attrs.src, alt: node.attrs.alt, title: node.attrs.title }]
    }
  })

const baseMarks = markdownSchema.spec.marks
const customMarks = baseMarks
  .update("strong", {
    ...baseMarks.get("strong"),
    toDOM() {
      return ["strong", { class: Strong }, 0]
    }
  })
  .update("code", {
    ...baseMarks.get("code"),
    toDOM() {
      return ["code", { class: Code }, 0]
    }
  })
  .update("link", {
    ...baseMarks.get("link"),
    toDOM(node: Mark) {
      return ["a", { class: Link, href: node.attrs.href, title: node.attrs.title }, 0]
    }
  })
  .addToEnd("strikethrough", {
    parseDOM: [
      { tag: "s" },
      { tag: "del" },
      { style: "text-decoration=line-through" }
    ],
    toDOM() {
      return ["s", {}, 0]
    }
  } as MarkSpec)

const ProseMirrorHeading = css`
  &:first-child {
    margin: 0;
  }
`

const ProseMirrorPre = css`
  border: 1px solid var(--separator-color);
`

const ProseMirrorOl = css`
  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const ProseMirrorUl = css`
  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`

export const schema = new Schema({
  nodes: customNodes,
  marks: customMarks
})

export const parser = new MarkdownParser(schema, defaultMarkdownParser.tokenizer, {
  ...defaultMarkdownParser.tokens,
  s: { mark: "strikethrough" }
})

export const serializer = new MarkdownSerializer(defaultMarkdownSerializer.nodes, {
  ...defaultMarkdownSerializer.marks,
  strikethrough: {
    open: "~~",
    close: "~~",
    mixable: true,
    expelEnclosingWhitespace: true
  }
})
